// src/wallet/web3modal.js
import { createAppKit } from "@reown/appkit/react";
import { EthersAdapter } from "@reown/appkit-adapter-ethers";
import {
  defineChain,
  mainnet,
  arbitrum,
  base,
  optimism,
  polygon,
  avalanche,
  bsc,
} from "@reown/appkit/networks";

const projectId = "971e9fbd3bf6815c225c6e914707ed24";

const metadata = {
  name: "Krionex",
  description: "Contract Deployment Infrastructure",
  url: "https://krionex.com",
  icons: [],
};

/**
 * Mainnets already supported in your app
 */
const supportedMainnets = [
  mainnet,
  arbitrum,
  base,
  optimism,
  polygon,
  avalanche,
  bsc,
];

/**
 * Testnets explicitly defined so AppKit/Reown treats them as supported networks too.
 * This prevents the "Switch Network" modal from appearing after successful actions
 * on testnets that your app already supports.
 *
 * Chain IDs:
 * - Sepolia: 11155111
 * - Arbitrum Sepolia: 421614
 * - Base Sepolia: 84532
 * - OP Sepolia: 11155420
 * - Polygon Amoy: 80002
 * - Avalanche Fuji: 43113
 * - BNB Smart Chain Testnet: 97
 */
const sepolia = defineChain({
  id: 11155111,
  caipNetworkId: "eip155:11155111",
  chainNamespace: "eip155",
  name: "Sepolia",
  nativeCurrency: {
    name: "Sepolia Ether",
    symbol: "ETH",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ["https://ethereum-sepolia-rpc.publicnode.com"],
    },
  },
  blockExplorers: {
    default: {
      name: "Etherscan",
      url: "https://sepolia.etherscan.io",
    },
  },
  testnet: true,
});

const arbitrumSepolia = defineChain({
  id: 421614,
  caipNetworkId: "eip155:421614",
  chainNamespace: "eip155",
  name: "Arbitrum Sepolia",
  nativeCurrency: {
    name: "Arbitrum Sepolia Ether",
    symbol: "ETH",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ["https://sepolia-rollup.arbitrum.io/rpc"],
    },
  },
  blockExplorers: {
    default: {
      name: "Arbiscan",
      url: "https://sepolia.arbiscan.io",
    },
  },
  testnet: true,
});

const baseSepolia = defineChain({
  id: 84532,
  caipNetworkId: "eip155:84532",
  chainNamespace: "eip155",
  name: "Base Sepolia",
  nativeCurrency: {
    name: "Base Sepolia Ether",
    symbol: "ETH",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ["https://sepolia.base.org"],
    },
  },
  blockExplorers: {
    default: {
      name: "BaseScan",
      url: "https://sepolia.basescan.org",
    },
  },
  testnet: true,
});

const optimismSepolia = defineChain({
  id: 11155420,
  caipNetworkId: "eip155:11155420",
  chainNamespace: "eip155",
  name: "OP Sepolia",
  nativeCurrency: {
    name: "OP Sepolia Ether",
    symbol: "ETH",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ["https://sepolia.optimism.io"],
    },
  },
  blockExplorers: {
    default: {
      name: "OP Sepolia Etherscan",
      url: "https://sepolia-optimism.etherscan.io",
    },
  },
  testnet: true,
});

const polygonAmoy = defineChain({
  id: 80002,
  caipNetworkId: "eip155:80002",
  chainNamespace: "eip155",
  name: "Polygon Amoy",
  nativeCurrency: {
    name: "POL",
    symbol: "POL",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ["https://rpc-amoy.polygon.technology"],
    },
  },
  blockExplorers: {
    default: {
      name: "OKX Explorer",
      url: "https://www.oklink.com/amoy",
    },
  },
  testnet: true,
});

const avalancheFuji = defineChain({
  id: 43113,
  caipNetworkId: "eip155:43113",
  chainNamespace: "eip155",
  name: "Avalanche Fuji",
  nativeCurrency: {
    name: "Avalanche",
    symbol: "AVAX",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ["https://api.avax-test.network/ext/bc/C/rpc"],
    },
  },
  blockExplorers: {
    default: {
      name: "SnowTrace",
      url: "https://testnet.snowtrace.io",
    },
  },
  testnet: true,
});

const bscTestnet = defineChain({
  id: 97,
  caipNetworkId: "eip155:97",
  chainNamespace: "eip155",
  name: "BNB Smart Chain Testnet",
  nativeCurrency: {
    name: "BNB",
    symbol: "tBNB",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ["https://data-seed-prebsc-1-s1.binance.org:8545"],
    },
  },
  blockExplorers: {
    default: {
      name: "BscScan Testnet",
      url: "https://testnet.bscscan.com",
    },
  },
  testnet: true,
});

const supportedTestnets = [
  sepolia,
  arbitrumSepolia,
  baseSepolia,
  optimismSepolia,
  polygonAmoy,
  avalancheFuji,
  bscTestnet,
];

/**
 * Keep this list aligned with the networks your backend/app exposes.
 */
const networks = [...supportedMainnets, ...supportedTestnets];

export const ethersAdapter = new EthersAdapter();

export const appKit = createAppKit({
  adapters: [ethersAdapter],
  networks,
  metadata,
  projectId,
  features: {
    analytics: false,
  },
});

if (typeof window !== "undefined") {
  window.appKit = appKit;
}